"use strict";

var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _isNil = _interopRequireDefault(require("lodash/isNil"));
var _axios = _interopRequireDefault(require("axios"));
var _axiosRetry = _interopRequireDefault(require("axios-retry"));
var _storage = _interopRequireDefault(require("../storage/storage"));
var _url = _interopRequireDefault(require("../url/url"));
const clientFingerprintName = 'sh-client-fingerprint';
const firewallRetryCondition = error => {
  return !(0, _isNil.default)(error.response) && error.response.status === 456;
};
const buildConfig = ({
  baseUrl,
  additionalHeaders,
  xsrfCookieName = 'csrftoken',
  xsrfHeaderName = 'X-CSRFToken',
  ...args
}) => {
  const clientFingerprint = _storage.default.get(clientFingerprintName, 'cookie');
  const config = {
    baseURL: baseUrl,
    headers: {
      ...additionalHeaders
    },
    validateStatus: status => {
      return status < 500 && status !== 456;
    },
    xsrfCookieName,
    xsrfHeaderName,
    ...args
  };
  if (!(0, _isNil.default)(clientFingerprint)) {
    config.headers[clientFingerprintName] = clientFingerprint;
  }
  return config;
};

/**
 * Utilities for working with APIs.
 * @module APIUtils
 */
class AxiosInstance {
  constructor(config) {
    this._axiosInstance = null;
    if (!(0, _isNil.default)(config)) {
      this._axiosInstance = this.init(config);
    }
  }

  /**
   * Creates a default instance of APIUtils.
   * @static
   * @function init
   * @param {Object} config - The configuration object.
   * @param {String} config.baseUrl - The base URL for all API requests in this instance.
   * @param {Object} [config.additionalHeaders] - Additional headers to add to every request.
   * @param {String} [config.xsrfCookieName='csrftoken'] - The name of the cookie to use as a value for the XSRF token.
   * @param {String} [config.xsrfHeaderName='X-CSRFToken'] - The name of the HTTP header that carries the XSRF token value.
   * @example
   * const serviceInstance = APIUtils.init({
   *     baseUrl: 'api/v1',
   *     additionalHeaders: {
   *         UserAgent: 'header-name'
   *     }
   * });
   * @returns {Object} - The axios instance.
   */
  init = config => {
    this._axiosInstance = _axios.default.create(buildConfig(config));
    this.attachInterceptor({
      method: 'request',
      fulfilled: currentConfig => {
        if (currentConfig.baseURL.toLowerCase().startsWith('http')) {
          // Non-relative call, remove the fingerprint to avoid possible CORS issues.
          delete currentConfig.headers[clientFingerprintName];
        }
        return currentConfig;
      },
      rejected: error => {
        return Promise.reject(error);
      }
    });
    this.attachInterceptor({
      method: 'response',
      fulfilled: response => {
        if (response.status >= 400 && response.status < 500) {
          return Promise.reject(response);
        } else {
          return response;
        }
      },
      rejected: error => {
        return Promise.reject(error);
      }
    });
    return this._axiosInstance;
  };

  /**
   * Creates a cancelable promise.
   * @static
   * @function makeCancelable
   * @param {Promise} promise - The promise to make cancelable.
   * @example
   * const dataLoader = APIUtils.makeCancelable(
   *     APIUtils.get('api/v1/test')
   * );
   *
   * dataLoader.cancel();
   * @returns {Object} - An object containing the `promise` as well as the `cancel` method.
   */
  makeCancelable(promise) {
    let hasCanceled = false;
    const wrappedPromise = new Promise((resolve, reject) => {
      promise.then(val => {
        if (hasCanceled) {
          reject({
            isCanceled: true
          }); // eslint-disable-line prefer-promise-reject-errors
        } else {
          resolve(val);
        }
      }).catch(error => {
        if (hasCanceled) {
          reject({
            isCanceled: true
          }); // eslint-disable-line prefer-promise-reject-errors
        } else {
          reject(error);
        }
      });
    });
    return {
      promise: wrappedPromise,
      cancel() {
        hasCanceled = true;
      }
    };
  }

  /**
   * Attaches an interceptor of the specified method to the axios instance.
   * @static
   * @function attachInterceptor
   * @param {Object} [data={}] - The interceptor data.
   * @param {String} [data.method=null] - The type of method to attach the interceptor to ('request' or 'response').
   * @param {Function} [data.fulfilled=null] - The function to handle `then` for a `Promise`.
   * @param {Function} [data.rejected=null] - The function to handle `reject` for a `Promise`.
   * @example
   * APIUtils.attachInterceptor({
   *     method: 'request',
   *     fulfilled: config => {
   *         ...code here
   *
   *         return config;
   *     },
   *     rejected: error => {
   *         return Promise.reject(error);
   *     }
   * });
   * @returns {void}
   */
  attachInterceptor = ({
    method,
    fulfilled,
    rejected
  }) => {
    this._axiosInstance.interceptors[method].use(fulfilled, rejected);
  };

  /**
   * Creates a GET request.
   * @static
   * @function get
   * @param {String} path - The API call path.
   * @param {Object} [data=null] - The parameters to send to the API call.
   * @param {Object} [config=null] - Modifies the <a href="https://github.com/axios/axios#request-config">configuration</a> for the request.
   * @example
   * APIUtils.get('api/v1/test');
   * @returns {AxiosInstance} - The axios instance.
   */
  get = (path, data, config) => {
    return this._axiosInstance.get(path, {
      params: data,
      ...config
    });
  };

  /**
   * Creates a POST request.
   * @static
   * @function post
   * @param {String} path - The API call path.
   * @param {Object} [data=null] - The parameters to send to the API call.
   * @param {Object} [config=null] - Modifies the <a href="https://github.com/axios/axios#request-config">configuration</a> for the request.
   * @param {Boolean} [stringify=true] - Whether to encode the data object into a query string.
   * @example
   * APIUtils.post('api/v1/test');
   * @returns {AxiosInstance} - The axios instance.
   */
  post = (path, data, config, stringify = true) => {
    const headers = config && config.headers ? config.headers : null;
    const formattedData = stringify ? _url.default.createQueryString(data) : data;
    return this._axiosInstance.post(path, formattedData, {
      ...config,
      headers: {
        ...headers,
        'X-CSRFToken': _storage.default.get('csrftoken', 'cookie')
      }
    });
  };

  /**
   * Creates a PATCH request.
   * @static
   * @function patch
   * @param {String} path - The API call path.
   * @param {Object} [data=null] - The parameters to send to the API call.
   * @param {Object} [config=null] - Modifies the <a href="https://github.com/axios/axios#request-config">configuration</a> for the request.
   * @example
   * APIUtils.patch('api/v1/test');
   * @returns {AxiosInstance} - The axios instance.
   */
  patch = (path, data, config) => {
    return this._axiosInstance.patch(path, data, config);
  };

  /**
   * Creates a PUT request.
   * @static
   * @function put
   * @param {String} path - The API call path.
   * @param {Object} [data=null] - The parameters to send to the API call.
   * @param {Object} [config=null] - Modifies the <a href="https://github.com/axios/axios#request-config">configuration</a> for the request.
   * @example
   * APIUtils.put('api/v1/test');
   * @returns {AxiosInstance} - The axios instance.
   */
  put = (path, data, config) => {
    return this._axiosInstance.put(path, data, config);
  };

  /**
   * Creates a DELETE request.
   * @static
   * @function delete
   * @param {String} path - The API call path.
   * @param {Object} [config=null] - Modifies the <a href="https://github.com/axios/axios#request-config">configuration</a> for the request.
   * @example
   * APIUtils.delete('api/v1/test');
   * @returns {AxiosInstance} - The axios instance.
   */
  delete = (path, config) => {
    return this._axiosInstance.delete(path, config);
  };

  /**
   * Creates a promise that aggregates the results of multiple requests.
   * @static
   * @function all
   * @param {Array} requests - The API calls to make concurrently.
   * @example
   * APIUtils.all([getUserInfo(), getProductInfo()]);
   * @returns {Promise} - The resolved or pending Promise.
   */
  all = requests => {
    return Promise.all(requests);
  };

  /**
   * Adds the ability to intercept failed requests and retry them whenever possible.
   * @static
   * @function addRetry
   * @param {Object} [data={}] - The retry data. See <a href="https://github.com/softonic/axios-retry#options" target="_blank">retry options</a>.
   * @example
   * APIUtils.addRetry({
   *     retries: 10,
   *     retryDelay: retryCount => {
   *         return retryCount * 1000;
   *     }}
   * });
   * @returns {void}
   */
  addRetry = ({
    retries = 3,
    retryDelay = _axiosRetry.default.exponentialDelay,
    retryCondition = firewallRetryCondition,
    ...args
  }) => {
    (0, _axiosRetry.default)(this._axiosInstance, {
      retries,
      retryDelay,
      retryCondition,
      ...args
    });
  };

  /**
   * Allows access to cancel tokens from axios without having to install and manage axios in the project.
   * @static
   * @function getCancelToken
   * @example
   * APIUtils.getCancelToken();
   * @returns {CancelToken} - The axios cancel token.
   */
  getCancelToken = () => {
    return _axios.default.CancelToken;
  };
}
var _default = exports.default = AxiosInstance;